import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Collapse,
  Chip
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditOutlineIcon from '@mui/icons-material/EditOutlined';
import axios from '../../../../services/ApiService';
import toast from 'react-hot-toast';
import { Helmet } from 'react-helmet-async';
import LoadingSpinner from '../../../support/LoadingSpinner';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Swal from 'sweetalert2'
import { ProgramData, Workflow } from '../../../types/ProgramTypes';
import { BVTConstants } from '../../../../utils/BVTConstants';
import { paths } from '../../../../utils/paths';

export default function ViewProgramDetails() {
  const [programData, setProgramData] = useState<ProgramData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [expandedWorkflows, setExpandedWorkflows] = useState<Set<number>>(new Set());
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = location.state || {};

  useEffect(() => {
    if (id) {
      fetchProgramData(id);
    }
  }, [id]);

  const fetchProgramData = async (programId: number) => {
    try {
      setLoading(true);
      const response = await axios.post("/getProgram", programId);
      setProgramData(response.data);
    } catch (error) {
      setError('Failed to load programs.');
      console.error('Error fetching program data.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    navigate(-1);
  };

  const handleDeleteClick = (workflow: Workflow) => {
    Swal.fire({title: 'Archive Workflow ?', 
      text: 'Are you sure you want to archive the workflow ' + workflow.name + '?', 
      icon: 'question', 
      confirmButtonText: 'Archive', 
      cancelButtonText: 'Cancel', 
      showCancelButton: true }
    ).then((result) => {
      if(result['isConfirmed']){
        deleteWorkflow(workflow.id);
      }
    })
  };

  const deleteWorkflow = async (workflowId: number) => {
    try {
      setLoading(true);
      await axios.delete(`/deleteWorkflow/${workflowId}`);
      toast.success('Workflow archived successfully');
    } catch (error) {
      console.error('Error deleting workflow.');
    } finally {
      setLoading(false);
      fetchProgramData(id);
    }
  };

  const handleStatusChangeClick = (workflow: Workflow) => {
    const activeText: string = workflow.status === BVTConstants.ACTIVE ? 'Deactivate' : 'Activate';
    Swal.fire({title: activeText + ' Workflow ?', 
      text: 'Do you want to '+ activeText +' the Worflow ' + workflow.name + '?', 
      icon: 'question', 
      confirmButtonText: activeText, 
      cancelButtonText: 'Cancel', 
      showCancelButton: true }
    ).then((result) => {
      if(result['isConfirmed']){
        const req = {id: workflow.id, status: workflow.status === BVTConstants.ACTIVE ? BVTConstants.INACTIVE : BVTConstants.ACTIVE}
        changeStatus(req);
      }
    })
  };

  const changeStatus = async(req: any) => {
    try {
      setLoading(true);
      const response = await axios.post("/updateWorkflowStatus", req);
      if(response.status === 200){
        toast.success('Workflow updated successfully');
        fetchProgramData(id);
      }
    } catch (error) {
      console.error('Error updating workflow status.');
    } finally {
      setLoading(false);
    }
  }

  const handleExpandWorkflow = (workflowId: number) => {
    const newExpanded = new Set(expandedWorkflows);
    if (newExpanded.has(workflowId)) {
      newExpanded.delete(workflowId);
    } else {
      newExpanded.add(workflowId);
    }
    setExpandedWorkflows(newExpanded);
  };

  const handleEditWorkflow = (workflow: Workflow) => {
    navigate(paths.admin.workflowForm, { 
      state: { 
        programId: id, 
        programName: programData?.programName,
        workflowId: workflow.id,
        workflow: workflow
      } 
    });
  };

  return (
    <>
      <Helmet>
        <title>BVT Admin - View Program</title>
      </Helmet>
      {loading ? (
          <LoadingSpinner loading={loading} />
        ) : error ? (
          <Typography variant="h6" color="error" textAlign="center">
            {error}
          </Typography>
        ) : (
        <Box sx={{ padding: 3, position: 'relative', maxWidth: '1200px', margin: 'auto' }}>
          <IconButton
            sx={{
              position: 'absolute',
              top: 20,
              right: 20,
              backgroundColor: '#f5f5f5',
              borderRadius: '50%',
              padding: 1,
              boxShadow: 2,
              '&:hover': {
                backgroundColor: '#e0e0e0',
              },
            }}
            onClick={handleClose}
          >
            <CloseIcon sx={{ color: 'black' }} />
          </IconButton>

          <Card sx={{ width: '100%' }}>
            <CardHeader
              title={programData.programName}
              subheader={programData.description}
              sx={{
                backgroundColor: '#f5f5f5',
                color: 'black',
                boxShadow: 1,
                borderTopLeftRadius: 2,
                borderTopRightRadius: 2,
              }}
            />
            <Divider />
            <CardContent sx={{marginTop: 1, paddingTop: 0}}>
              <Typography variant='caption' sx={{color: programData.status === 'ACTIVE' ? 'lightgreen' : 'red'}}>
                  {programData.status}
              </Typography>
              
              {/* Category Chips */}
              {programData.categories && programData.categories.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1, mb: 2}}>
                  {programData.categories.map((category) => (
                    <Chip
                      key={category.catId}
                      label={category.catName}
                      size="small"
                      variant="outlined"
                      sx={{ 
                        backgroundColor: '#f5f5f5',
                        borderColor: '#ddd',
                        '&:hover': {
                          backgroundColor: '#e0e0e0'
                        }
                      }}
                    />
                  ))}
                </Box>
              )}

              { programData.workflows.length === 0 ? (
                <Typography variant="body1" gutterBottom>
                  No Workflows
                </Typography>
              ):(
                <></>
              )}
              {programData.workflows.map((workflow) => (
                <Box key={workflow.id} sx={{ marginBottom: 3, padding: 2, backgroundColor: '#e6e6e6', borderRadius: 2 }}>
                  {/* Workflow Header - Always Visible */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 1
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', marginRight: 2 }}>
                        {workflow.name}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{
                          color: workflow.status === 'ACTIVE' ? 'green' : 
                                 workflow.status === 'DRAFT' ? 'orange' : 'red',
                          fontWeight: 'bold',
                          padding: '2px 8px',
                          backgroundColor: workflow.status === 'ACTIVE' ? '#e8f5e8' : 
                                          workflow.status === 'DRAFT' ? '#fff3e0' : '#ffebee',
                          borderRadius: '12px',
                          marginRight: 2
                        }}
                      >
                        {workflow.status}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {workflow.steps.length} steps • {workflow.payment.toFixed(2)}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {/* Edit button for DRAFT workflows */}
                      {workflow.status === 'DRAFT' && (
                        <Tooltip title="Edit Workflow" arrow>
                          <Button
                            variant="outlined"
                            color="info"
                            sx={{ marginRight: 1 }}
                            onClick={() => handleEditWorkflow(workflow)}
                          >
                            Edit{/* <EditOutlineIcon /> */}
                          </Button>
                        </Tooltip>
                      )}
                      
                      {/* Activate/Deactivate button for non-DRAFT workflows */}
                      {workflow.status !== 'DRAFT' && (
                        <Button
                          variant="outlined"
                          sx={{ marginRight: 1 }}
                          onClick={() => handleStatusChangeClick(workflow)}
                        >
                          {workflow.status === BVTConstants.ACTIVE ? 'Deactivate' : 'Activate'}
                        </Button>
                      )}
                      
                      <Tooltip title="Archive Workflow" arrow>
                        <Button
                          variant="outlined"
                          color="error"
                          sx={{ marginRight: 1 }}
                          onClick={() => handleDeleteClick(workflow)}
                        >
                          <DeleteOutlineIcon />
                        </Button>
                      </Tooltip>
                      
                      <IconButton
                        onClick={() => handleExpandWorkflow(workflow.id)}
                        sx={{
                          transform: expandedWorkflows.has(workflow.id) ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s'
                        }}
                      >
                        <ExpandMoreIcon />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Expandable Content */}
                  <Collapse in={expandedWorkflows.has(workflow.id)}>
                    <Box sx={{ marginTop: 2, paddingTop: 2, borderTop: '1px solid #ccc' }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Description: {workflow.description}
                      </Typography>
                      <Typography variant="subtitle2" gutterBottom>
                        Type: {workflow.type}
                      </Typography>
                      <Typography variant="subtitle2" gutterBottom>
                        Allow Self Subscription: {workflow.selfSubscribe ? 'Yes' : 'No'}
                      </Typography>
                      {workflow.instructions && (
                        <>
                          <Typography variant="subtitle2" gutterBottom>
                            Show Instructions After Completion: {workflow.showInstructionsAfterCompletion ? 'Yes' : 'No'}
                          </Typography>
                          <Typography variant="subtitle2" gutterBottom>
                            Instructions:
                          </Typography>
                          <Box
                            sx={{
                              height: '120px',
                              overflow: 'auto',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              padding: '12px',
                              backgroundColor: '#f9f9f9',
                              marginBottom: 2
                            }}
                          >
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {workflow.instructions}
                            </Typography>
                          </Box>
                        </>
                      )}
                      
                      {workflow.steps.map((step) => (
                        <Box key={step.seqId} sx={{ marginBottom: 2, padding: 2, backgroundColor: '#ffffff', borderRadius: 2 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
                            Step {step.seqId}: {step.stepName}
                          </Typography>
                          <Typography variant="subtitle2" gutterBottom>
                            Action: {step.stepActionType}
                          </Typography>
                          <Typography variant="subtitle2" gutterBottom>
                            Action On: {step.stepActionOn}
                          </Typography>
                          <Typography variant="subtitle2" gutterBottom>
                            Instructions: {step.instructions}
                          </Typography>
                          {step.assessmentId > 0 ? (
                            <>
                              <Typography variant="subtitle2" gutterBottom>
                                Assessment: {step.assessment.name}
                              </Typography>
                              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold'}}>
                                Score Ranges:
                              </Typography>
                            </>
                          ):(
                            <></>
                          )}

                          {step.scoreRanges.map((scoreRange) => (
                            <Paper key={scoreRange.id} sx={{ padding: 1, marginBottom: 1, backgroundColor: '#f3f3f3' }}>
                              <Typography variant="body2">
                                {scoreRange.name} (From: {scoreRange.minVal} To: {scoreRange.maxVal})
                              </Typography>
                            </Paper>
                          ))}
                        </Box>
                      ))}
                    </Box>
                  </Collapse>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Box>
        )}
    </>
  );
}
